/**
 * Speaker definitions (D192) — the voices a pack speaks in.
 *
 * @remarks
 * A line's `source` says who is speaking. Four sources are the ENGINE's and
 * fixed — `environment`, `human_log`, `player`, `system` — because the engine
 * itself speaks in them: the world's answers, a found document, the player's
 * echo, the terminal's chrome. Everything else that speaks is a SPEAKER the
 * pack declares here: a narrator, a voice on the radio, a companion. Calder's
 * D149 §7.4 said what a narrator is — _a channel with an index and a
 * quotation law, not a character object_ — and this is that channel,
 * declared by the pack rather than named by the schema. Until D192 the
 * schema named one: `eurydice`, the POC's character, in the engine's own
 * enum, so a second game could not have a narrator of its own.
 *
 * **Speakers are optional** (Mike's ruling, 2026-09-17: builders may opt
 * out). A pack that declares none has no Voices dialog and no quotation law,
 * and every line it speaks is in one of the four fixed kinds.
 *
 * What a speaker carries:
 *
 * - `speakerId` — the word the pack's lines use as `source`. A content id,
 *   never one of the four fixed kinds.
 * - `name` — what the player sees: the Voices dialog's heading, the export's
 *   label. Chrome for the audit.
 * - `isQuoted` — the D84 quotation law: every reading of every line this
 *   speaker says is wrapped in quotation marks, and the audit refuses one
 *   that is not. The quotes are the one textual signature that survives
 *   every medium (monochrome, screen reader, copy-paste, print).
 * - `reveal` — how the speaker's lines arrive on screen (R27's cadence table,
 *   moved out of the engine). Absent, stepped at 14 ms: the metronome Mike
 *   ruled for her (D99), as the default any speaker gets.
 * - `color` — the voice tint, a hex triplet. Absent, the shell's default
 *   voice color.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { speakerIdSchema } from './identifiers.js';
import { nonBlankStringSchema, positiveIntegerSchema } from './primitives.js';

/** How a speaker's text arrives on screen (R27): a soft focus, no animation at all, or character-by-character. */
export const revealCadenceSchema = z.enum(['focus', 'instant', 'stepped']);

export type RevealCadence = z.infer<typeof revealCadenceSchema>;

/**
 * One speaker's reveal: the cadence, and for `stepped` the pace in
 * milliseconds per character.
 */
export const speakerRevealSchema = z
	.strictObject({
		cadence: revealCadenceSchema,
		stepMs: positiveIntegerSchema.optional(),
	})
	.readonly()
	.refine((reveal) => reveal.cadence === 'stepped' || reveal.stepMs === undefined, {
		message: '`stepMs` is the pace of a `stepped` reveal; a `focus` or `instant` reveal has no pace',
		path: ['stepMs'],
	});

export type SpeakerReveal = z.infer<typeof speakerRevealSchema>;

/** One declared speaker — see the module remarks. */
export const speakerDefinitionSchema = z
	.strictObject({
		color: z.string().regex(/^#[0-9a-fA-F]{6}$/u).optional(),
		isQuoted: z.boolean().default(false),
		name: nonBlankStringSchema,
		reveal: speakerRevealSchema.optional(),
		speakerId: speakerIdSchema,
	})
	.readonly();

/** One declared speaker — see {@link speakerDefinitionSchema}. */
export type SpeakerDefinition = z.infer<typeof speakerDefinitionSchema>;
